/**
 * Mock data for the project Report / Metrics dashboard.
 */
import { ProjectDto } from '../../api/model';
import { Status, DeliverObjectCriticality } from '../../api/model';
import { taskStatusColors, status } from './reportTheme';

export interface KpiDatum {
  key: 'projectProgress' | 'tasksCompleted' | 'openActionPoints' | 'onTimeValidation';
  value: string;
  delta: string;
  higherIsBetter: boolean;
  direction: 'up' | 'down' | 'flat';
}

export interface TrendPoint {
  period: string;
  raised: number;
  resolved: number;
}

export interface CategoryDatum {
  label: string;
  value: number;
}

export interface StatusDatum {
  status: string;
  value: number;
  color: string;
}

export interface DeliverableCriticalityDatum {
  label: string;
  counts: Record<string, number>;
}

export const DELIVERABLE_CRITICALITY_ORDER: string[] = [
  DeliverObjectCriticality.EASY,
  DeliverObjectCriticality.MIDDLE,
  DeliverObjectCriticality.HIGH,
];

export type ProjectHealthKey = 'onTrack' | 'watch' | 'atRisk' | 'critical';

export function getProjectHealthStatus(score: number): { key: ProjectHealthKey; color: string } {
  if (score >= 80) return { key: 'onTrack', color: status.good };
  if (score >= 65) return { key: 'watch', color: status.warning };
  if (score >= 45) return { key: 'atRisk', color: status.serious };
  return { key: 'critical', color: status.critical };
}

export interface ReportData {
  health: number;
  kpis: KpiDatum[];
  actionPointTrend: TrendPoint[];
  taskStatus: StatusDatum[];
  phaseProgress: CategoryDatum[];
  deliverablesByPhase: DeliverableCriticalityDatum[];
}

function seedFrom(project?: ProjectDto): number {
  const basis = project?.id ?? project?.name?.length ?? 7;
  return Math.abs(((basis * 1103515245 + 12345) >> 4) % 1000);
}

function vary(seed: number, salt: number, min: number, max: number): number {
  const n = Math.abs(Math.sin(seed + salt) * 10000);
  return min + Math.floor((n % 1) * (max - min + 1));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];


export const PRESET_PHASE_TYPES: string[] = [
  'INITIALIZATION',
  'PLANNING_AND_DESIGN',
  'DEVELOPMENT',
  'TESTING',
  'ACCEPTANCE',
  'HANDOVER',
];

export function buildReportData(project?: ProjectDto, fallbackPhaseNames?: string[]): ReportData {
  const seed = seedFrom(project);
  const fallbackPhases =
    fallbackPhaseNames && fallbackPhaseNames.length > 0 ? fallbackPhaseNames : PRESET_PHASE_TYPES;

  const realPhases = (project?.phases ?? []).filter((p) => p?.name || p?.customName);
  const phaseProgress: CategoryDatum[] =
    realPhases.length > 0
      ? realPhases.map((p) => ({
          label: p.customName || p.name || `Phase ${p.id ?? ''}`.trim(),
          value: Math.round(p.progress ?? 0),
        }))
      : fallbackPhases.map((label, i) => ({
          label,
          value: Math.max(0, Math.min(100, vary(seed, i, 8, 100) - i * 12)),
        }));

  const progressPct =
    typeof project?.progress === 'number'
      ? Math.round(project.progress)
      : Math.round(
          phaseProgress.reduce((s, p) => s + p.value, 0) / Math.max(1, phaseProgress.length),
        );

  const taskCounts: Record<string, number> = {
    [Status.FINISHED]: vary(seed, 11, 24, 60),
    [Status.IN_PROGRESS]: vary(seed, 12, 8, 22),
    [Status.PLANNED]: vary(seed, 13, 6, 18),
    [Status.DELAYED]: vary(seed, 14, 0, 6),
    [Status.BACKLOG]: vary(seed, 15, 4, 14),
  };
  const totalTasks = Object.values(taskCounts).reduce((a, b) => a + b, 0);
  const taskStatus: StatusDatum[] = Object.entries(taskCounts).map(([status, value]) => ({
    status,
    value,
    color: taskStatusColors[status],
  }));

  const actionPointTrend: TrendPoint[] = MONTHS.map((period, i) => ({
    period,
    raised: vary(seed, 20 + i, 3, 11),
    resolved: vary(seed, 40 + i, 2, 12),
  }));
  const openActionPoints = actionPointTrend.reduce((s, p) => s + (p.raised - p.resolved), 0);

  const deliverablesByPhase: DeliverableCriticalityDatum[] = phaseProgress.map((ph, i) => {
    const total = vary(seed, 60 + i, 4, 12);
    const high = vary(seed, 80 + i, 0, Math.min(3, total));
    const middle = vary(seed, 70 + i, 0, total - high);
    const easy = total - high - middle;
    return {
      label: ph.label,
      counts: {
        [DeliverObjectCriticality.EASY]: easy,
        [DeliverObjectCriticality.MIDDLE]: middle,
        [DeliverObjectCriticality.HIGH]: high,
      },
    };
  });

  const onTimeValidation = vary(seed, 4, 78, 97);
  const health = Math.max(0, Math.min(100, Math.round(progressPct * 0.55 + onTimeValidation * 0.45)));

  const kpis: KpiDatum[] = [
    {
      key: 'projectProgress',
      value: `${progressPct}%`,
      delta: `+${vary(seed, 1, 2, 9)}%`,
      higherIsBetter: true,
      direction: 'up',
    },
    {
      key: 'tasksCompleted',
      value: `${taskCounts[Status.FINISHED]}/${totalTasks}`,
      delta: `+${vary(seed, 2, 3, 12)}`,
      higherIsBetter: true,
      direction: 'up',
    },
    {
      key: 'openActionPoints',
      value: `${Math.max(0, openActionPoints)}`,
      delta: `-${vary(seed, 3, 1, 5)}`,
      higherIsBetter: false,
      direction: 'down',
    },
    {
      key: 'onTimeValidation',
      value: `${onTimeValidation}%`,
      delta: `+${vary(seed, 5, 1, 6)}%`,
      higherIsBetter: true,
      direction: 'up',
    },
  ];

  return { health, kpis, actionPointTrend, taskStatus, phaseProgress, deliverablesByPhase };
}
